import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { EmployeeService } from '../../services/employee.service';
import { LogService } from '../../services/log.service';

// Importaciones de los validadores.
import { passwordMatchValidator } from '../../validators/password-match.validator';

// Importaciones de interfaces.
import { IEmployee } from '../../interfaces/employee.interface';

// Importaciones de modelos.
import { Employee } from '../../models/employee.model';
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la actualización de contraseña del empleado.
 *
 * Este componente proporciona una interfaz para actualizar la contraseña de un empleado.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos del empleado seleccionado.
 */
@Component({
  selector: 'app-employee-update-password-dialog',
  templateUrl: './employee-update-password-dialog.component.html',
  standalone: false,
})
export class EmployeeUpdatePasswordDialogComponent implements OnInit {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de actualización de empleados.
   */
  private readonly dialogRef: MatDialogRef<EmployeeUpdatePasswordDialogComponent> =
    inject(MatDialogRef<EmployeeUpdatePasswordDialogComponent>);

  /**
   * Datos del empleado.
   *
   * Contiene la información del empleado que se está actualizando, obtenida a través de la inyección de dependencias.
   */
  private readonly employee: IEmployee = inject<IEmployee>(MAT_DIALOG_DATA);

  /**
   * Indica si la nueva contraseña debe estar oculta en el campo de entrada.
   */
  newPasswordHide: boolean = true;

  /**
   * Indica si la confirmación de la nueva contraseña debe estar oculta en el campo de entrada.
   */
  confirmNewPasswordHide: boolean = true;

  /**
   * Indica si la operación de actualizar la contraseña está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se actualiza la contraseña del empleado.
   */
  loading: boolean = false;

  /**
   * Lista de errores al actualizar la contraseña.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización de contraseña del empleado.
   *
   * Gestiona los campos y validaciones necesarias para actualizar la contraseña del empleado.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup(
    {
      employeeId: new FormControl('', Validators.required),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(
          '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}:;<>,.?~/-]).{8,}$'
        ),
      ]),
      confirmNewPassword: new FormControl('', Validators.required),
    },
    { validators: passwordMatchValidator('newPassword', 'confirmNewPassword') }
  );

  /**
   * Obtiene el control del formulario para el campo 'employeeId'.
   *
   * Retorna el control asociado a la propiedad 'employeeId' del formulario,
   * que gestiona la identificación del empleado.
   *
   * @returns El control de formulario para 'employeeId'.
   */
  get formEmployeeId(): AbstractControl | null {
    return this.form.get('employeeId');
  }

  /**
   * Obtiene el control del formulario correspondiente a la nueva contraseña.
   *
   * @returns El control de `newPassword` o `null` si no existe.
   */
  get formNewPassword(): AbstractControl | null {
    return this.form.get('newPassword');
  }

  /**
   * Obtiene el control del formulario correspondiente a la confirmación de la nueva contraseña.
   *
   * @returns El control de `confirmNewPassword` o `null` si no existe.
   */
  get formConfirmNewPassword(): AbstractControl | null {
    return this.form.get('confirmNewPassword');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param employeeService - Servicio para gestionar la información de los empleados.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private employeeService: EmployeeService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta cuando el componente es inicializado. Es utilizado para realizar tareas
   * de configuración o cargar datos necesarios para la operación del componente.
   */
  async ngOnInit(): Promise<void> {
    this.formEmployeeId?.setValue(this.employee.id);
  }

  /**
   * Actualiza la contraseña del empleado.
   *
   * Este método se encarga de realizar el proceso de actualizar la contraseña del empleado.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async update(): Promise<void> {
    this.loading = true;
    this.errors = [];

    const employee: IEmployee = new Employee({
      id: Number(this.formEmployeeId?.value),
      password: this.formNewPassword?.value,
    });

    try {
      const employeeResponse: string = await this.employeeService.patchPassword(
        employee
      );

      // Añadir un registro de actualización de contraseña del empleado.
      try {
        await this.logService.create(
          new Log({
            description: `Ha actualizado la contraseña del empleado '${this.employee.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de actualización de contraseña del empleado.'
          );
        }
      }

      this.snackBar.open(employeeResponse, '', {
        duration: 5000,
      });

      this.dialogRef.close();
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
